import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import api from '../services/api';
import { useDirectory } from '../hooks/useDirectory';
import PageAccessBulk from './PageAccessBulk';

// Platform > Page access — the Nano Utilities permissions model: Entra groups
// decide who can sign in; pages listed here are granted person by person.
// Nobody holds a page until they are ticked (full admins see every page
// anyway), so a page can be live in production and switched on for a pilot one
// name at a time. Full admins only — the dashboard server gates it
// (routes/pagePermissions.js) and so does FSM, which owns the grants.

const ukDateTime = (d) => {
  if (!d) return '';
  const date = new Date(String(d).replace(' ', 'T'));
  if (Number.isNaN(date.getTime())) return String(d);
  return date.toLocaleString('en-GB', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};

const errorText = (err) => err?.response?.data?.error || err?.message || 'Something went wrong.';

function PageAccess() {
  const queryClient = useQueryClient();
  const [pageId, setPageId] = useState('jobcards');
  const [search, setSearch] = useState('');
  const [onlyHolders, setOnlyHolders] = useState(false);
  const [saveError, setSaveError] = useState(null);
  // 'page': one page at a time; 'bulk': everyone x every page, ticked and saved at once.
  const [mode, setMode] = useState('page');

  const pagesQuery = useQuery({
    queryKey: ['pagePermissions', 'pages'],
    queryFn: async () => (await api.get('/api/page-permissions/pages')).data.pages,
    retry: false,
  });

  const pageQuery = useQuery({
    queryKey: ['pagePermissions', 'page', pageId],
    queryFn: async () => (await api.get(`/api/page-permissions/pages/${pageId}`)).data,
    staleTime: 0,
    retry: false,
  });

  // Everyone who can be ticked: the company directory (Microsoft 365 staff),
  // plus anyone already holding the page who isn't in it, so a grant can
  // always be seen and taken away.
  const directory = useDirectory();

  const page = pageQuery.data?.page;
  const holders = useMemo(() => pageQuery.data?.people ?? [], [pageQuery.data]);
  const holderByOid = useMemo(
    () => new Map(holders.map((h) => [h.userOid.toLowerCase(), h])),
    [holders]
  );

  const people = useMemo(() => {
    const byOid = new Map();
    for (const p of directory.data?.people ?? []) {
      if (!p.entra_user_id || !p.display_name) continue;
      const key = p.entra_user_id.toLowerCase();
      byOid.set(key, {
        userOid: key,
        displayName: p.display_name,
        detail: p.job_title || p.department || p.email || null,
      });
    }
    for (const h of holders) {
      const key = h.userOid.toLowerCase();
      if (!byOid.has(key)) byOid.set(key, { userOid: key, displayName: h.displayName || key, detail: null });
    }
    return [...byOid.values()].sort((a, b) => a.displayName.localeCompare(b.displayName));
  }, [directory.data, holders]);

  const q = search.trim().toLowerCase();
  const shown = people.filter((p) => {
    if (onlyHolders && !holderByOid.has(p.userOid)) return false;
    if (!q) return true;
    return p.displayName.toLowerCase().includes(q) || (p.detail || '').toLowerCase().includes(q);
  });

  const setLevel = useMutation({
    mutationFn: async ({ person, level }) =>
      (await api.put(`/api/page-permissions/pages/${pageId}/people/${person.userOid}`, {
        level,
        displayName: person.displayName,
      })).data,
    onMutate: () => setSaveError(null),
    onError: (err, { person }) => setSaveError(`${person.displayName}: ${errorText(err)}`),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['pagePermissions', 'page', pageId] }),
  });

  const topLevel = page ? page.levels[page.levels.length - 1] : 'edit';
  const toggle = (person) => {
    const holding = holderByOid.has(person.userOid);
    setLevel.mutate({ person, level: holding ? 'none' : topLevel });
  };

  if (pagesQuery.isError || pageQuery.isError) {
    const err = pagesQuery.error || pageQuery.error;
    return (
      <div className="w-full p-6">
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-800">
          {err?.response?.status === 403
            ? 'This page is for full administrators only.'
            : `Couldn’t load page access: ${errorText(err)} Try again in a minute.`}
        </div>
      </div>
    );
  }

  return (
    <div className="w-full p-6">
      <h1 className="mb-1 text-2xl font-bold text-gray-900">Page access</h1>
      <p className="mb-4 text-sm text-gray-500">
        Who can use each page, person by person. Sign-in still comes from the Entra groups; a page
        here is held only once someone is ticked. Full admins see every page anyway.
      </p>

      <div className="mb-4 inline-flex rounded-lg border border-gray-200 bg-white p-1 text-sm">
        {[['page', 'One page'], ['bulk', 'Bulk']].map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => setMode(key)}
            className={`rounded-md px-3 py-1.5 font-medium ${mode === key ? 'bg-brand text-white' : 'text-gray-600 hover:bg-gray-100'}`}
          >
            {label}
          </button>
        ))}
      </div>

      {mode === 'bulk' ? (
        <PageAccessBulk pages={pagesQuery.data ?? []} directory={directory} />
      ) : (
      <>

      <div className="mb-4 rounded-lg border border-gray-200 bg-white p-4">
        <label className="mb-1 block text-sm font-semibold text-gray-900" htmlFor="page-access-page">Page</label>
        <select
          id="page-access-page"
          value={pageId}
          onChange={(e) => { setPageId(e.target.value); setSaveError(null); }}
          className="w-full rounded-md border border-gray-300 px-2.5 py-2 text-sm sm:w-80"
        >
          {(pagesQuery.data ?? [{ id: 'jobcards', label: 'Job Cards' }]).map((p) => (
            <option key={p.id} value={p.id}>{p.label}</option>
          ))}
        </select>
        {page?.description && <p className="mt-2 text-sm text-gray-500">{page.description}</p>}
        <p className="mt-2 text-sm text-gray-700">
          <span className="font-semibold">{holders.length}</span> {holders.length === 1 ? 'person has' : 'people have'} access.
        </p>
      </div>

      <div className="mb-6 rounded-lg border border-gray-200 bg-white">
        <div className="flex flex-col gap-3 border-b border-gray-200 p-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="relative sm:w-80">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Narrow by name or role…"
              className="w-full rounded-md border border-gray-300 py-2 pl-8 pr-2.5 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
            />
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input type="checkbox" checked={onlyHolders} onChange={(e) => setOnlyHolders(e.target.checked)} className="h-4 w-4 rounded border-gray-300 text-brand focus:ring-brand" />
            Only people with access
          </label>
        </div>
        {saveError && <p className="border-b border-gray-200 bg-red-50 px-4 py-2 text-sm text-red-700">Couldn’t save — {saveError}</p>}
        {(pageQuery.isLoading || directory.isLoading) && <p className="p-4 text-sm text-gray-400">Loading…</p>}
        {directory.isError && (
          <p className="p-4 text-sm text-amber-700">The staff directory couldn’t be loaded, so only people who already have access are shown.</p>
        )}
        <ul className="max-h-[32rem] divide-y divide-gray-100 overflow-y-auto">
          {shown.map((person) => {
            const holder = holderByOid.get(person.userOid);
            const pending = setLevel.isPending && setLevel.variables?.person.userOid === person.userOid;
            return (
              <li key={person.userOid} className="flex items-center justify-between gap-3 px-4 py-2.5">
                <span className="min-w-0">
                  <span className="block truncate text-sm font-medium text-gray-900">{person.displayName}</span>
                  <span className="block truncate text-xs text-gray-500">
                    {[person.detail, holder && `added by ${holder.grantedByName || 'unknown'} ${ukDateTime(holder.grantedAt)}`].filter(Boolean).join(' · ')}
                  </span>
                </span>
                <label className="flex shrink-0 items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={Boolean(holder)}
                    disabled={pending}
                    onChange={() => toggle(person)}
                    className="h-5 w-5 rounded border-gray-300 text-brand focus:ring-brand disabled:opacity-50"
                  />
                  {pending ? 'Saving…' : 'Has access'}
                </label>
              </li>
            );
          })}
          {!pageQuery.isLoading && !directory.isLoading && shown.length === 0 && (
            <li className="px-4 py-6 text-center text-sm text-gray-500">
              {onlyHolders && !q ? 'Nobody has this page yet.' : 'No names match.'}
            </li>
          )}
        </ul>
      </div>

      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <h2 className="mb-3 text-base font-semibold text-gray-900">History</h2>
        {(pageQuery.data?.history ?? []).length === 0 ? (
          <p className="text-sm text-gray-500">No changes yet.</p>
        ) : (
          <ul className="space-y-1.5">
            {pageQuery.data.history.map((h) => (
              <li key={h.id} className="text-sm text-gray-700">
                <span className="text-gray-400">{ukDateTime(h.changedAt)}</span> — {h.summary}
              </li>
            ))}
          </ul>
        )}
      </div>
      </>
      )}
    </div>
  );
}

export default PageAccess;
